import React, { useRef, useMemo, useCallback, useEffect, useState } from 'react';
import { useStripeAlert } from '../settings/stripeStatus';
import { WarnMark, needsSentence } from '../settings/StripeStatusCard';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import SlidePanel from '../shared/SlidePanel';
import NotificationItem from './NotificationItem';
import { useNotificationPoll } from './useNotificationPoll';
import { usePullToRefresh } from './usePullToRefresh';
import { notificationsActions } from '../../_actions/notifications.actions';
import { useTodos, refreshTodos } from './todos';
import { useReception } from '../../_utils/receptionMode';
import { BaileyStill } from '../agent/bailey/BaileyAvatar';
import { useSavedLook } from '../agent/bailey/lookStore';
import { hapticTick } from '../../_utils/haptics';
import './notifications.css';

const groupByBucket = (items) => {
  const startOfToday = moment().startOf('day').valueOf();
  const startOfYesterday = moment().subtract(1, 'day').startOf('day').valueOf();
  const groups = { today: [], yesterday: [], earlier: [] };
  items.forEach(i => {
    if (i.timestamp >= startOfToday) groups.today.push(i);
    else if (i.timestamp >= startOfYesterday) groups.yesterday.push(i);
    else groups.earlier.push(i);
  });
  return groups;
};

const EmptyState = () => (
  <div className="notif-empty">
    <div className="notif-empty-icon">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    </div>
    <p className="notif-empty-title">You're all caught up</p>
    <p className="notif-empty-sub">New activity will land here</p>
  </div>
);

const SkeletonRows = () => (
  <div className="notif-skeleton">
    {[0, 1, 2].map(i => (
      <div key={i} className="notif-skeleton-row">
        <span className="notif-skeleton-icon" />
        <span className="notif-skeleton-body">
          <span className="notif-skeleton-line notif-skeleton-line--title" />
          <span className="notif-skeleton-line notif-skeleton-line--sub" />
        </span>
      </div>
    ))}
  </div>
);

// To do icons: one stroke glyph per kind, on the dark Bailey tile.
const TODO_ICON = {
  setup: <path d="M12 3l1.8 4.6L18 9.4l-4.2 1.8L12 16l-1.8-4.8L6 9.4l4.2-1.8z" />,
  post: <><rect x="3" y="3" width="18" height="18" rx="4" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5L5 21" /></>,
  ask: <><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /><path d="M9 12h.01M12 12h.01M15 12h.01" /></>,
  reply: <><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /></>,
  repeat: <><path d="M4 12a8 8 0 0 1 13.7-5.6L20 9M20 4v5h-5" /><path d="M20 12a8 8 0 0 1-13.7 5.6L4 15M4 20v-5h5" /></>,
};
const TodoIcon = ({ kind }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{TODO_ICON[kind] || TODO_ICON.setup}</svg>
);
const SetupRing = ({ done, total }) => {
  const r = 18; const c = 2 * Math.PI * r; const p = total ? Math.min(1, done / total) : 0;
  return (
    <svg className="nt-setup-ring" width="46" height="46" viewBox="0 0 44 44" aria-hidden="true">
      <circle cx="22" cy="22" r={r} className="nt-ring-track" />
      <circle cx="22" cy="22" r={r} className="nt-ring-fill" strokeDasharray={`${(p * c).toFixed(1)} ${c.toFixed(1)}`} transform="rotate(-90 22 22)" />
      <text x="22" y="26.5" textAnchor="middle">{done}/{total}</text>
    </svg>
  );
};

const NotificationPanel = ({ onClose }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { items, lastViewedAt, loading } = useSelector(s => s.notifications);
  const scrollRef = useRef(null);
  const slidePanelRef = useRef(null);

  useNotificationPoll(true);
  const todos = useTodos(true);
  const look = useSavedLook();
  // A locked front desk without Bailey: the card is plain, not Bailey's.
  const { baileyOff } = useReception();
  useEffect(() => { refreshTodos(true); }, []);

  // Fetch the full list once when the panel opens. The poll only
  // refreshes the unread count for the bell badge — opening the
  // panel is the trigger to actually pull the list payload.
  useEffect(() => {
    dispatch(notificationsActions.loadNotifications());
  }, [dispatch]);

  const onRefresh = useCallback(() => {
    return dispatch(notificationsActions.loadNotifications());
  }, [dispatch]);

  const pull = usePullToRefresh(scrollRef, onRefresh);

  const grouped = useMemo(() => groupByBucket(items || []), [items]);

  // Pinned while Stripe needs something: it is the one notification that
  // costs the salon money every day it is ignored, so it never scrolls away.
  const stripe = useStripeAlert();
  const fixStripe = () => { closeWithAnim(); setTimeout(() => stripe.fix(), 320); };

  // To do first when something is waiting; otherwise straight to Updates.
  const waiting = todos.count + (stripe.active ? 1 : 0);
  const [tab, setTab] = useState(() => (waiting > 0 ? 'todo' : 'updates'));
  const unread = (items || []).filter((i) => (i.isServerRow ? !i.isRead : i.timestamp > lastViewedAt)).length;
  const go = (row) => {
    hapticTick();
    closeWithAnim();
    setTimeout(() => { if (row.run) row.run(history); else if (row.to) history.push(row.to); }, 320);
  };
  const heroTitle = waiting === 0 ? 'Nothing needs you right now.'
    : waiting === 1 ? '1 thing needs you today.' : `${waiting} things need you today.`;

  const closeWithAnim = () => {
    if (slidePanelRef.current && slidePanelRef.current.handleClose) {
      slidePanelRef.current.handleClose();
    } else if (onClose) {
      onClose();
    }
  };

  const handleTap = (item) => {
    // Server-backed rows mark just THIS one read (server + store). Legacy
    // synthetic-id rows have no per-row read state, so they keep the old
    // lastViewedAt behaviour — which clears the lot, but it's all they had.
    // Never do both: markViewed() would visually clear every row while the
    // badge only dropped by one.
    if (item?.isServerRow) {
      dispatch(notificationsActions.markOneRead(item));
    } else {
      dispatch(notificationsActions.markViewed(Date.now()));
    }
    closeWithAnim();
    setTimeout(() => {
      if (item.linkTo?.path) {
        history.push(item.linkTo.path, item.linkTo.state || {});
      }
    }, 320);
  };

  const handleMarkAllRead = () => {
    dispatch(notificationsActions.markAllRead());
  };

  const renderGroup = (label, list, startIndex) => {
    if (!list.length) return null;
    return (
      <div className="notif-group" key={label}>
        <div className="notif-group-label">{label}</div>
        <div className="notif-group-list">
          {list.map((item, i) => (
            <NotificationItem
              key={item.id}
              item={item}
              index={startIndex + i}
              isUnread={item.isServerRow ? !item.isRead : item.timestamp > lastViewedAt}
              onTap={handleTap}
            />
          ))}
        </div>
      </div>
    );
  };

  const todayCount = grouped.today.length;
  const yesterdayCount = grouped.yesterday.length;
  const hasItems = items && items.length > 0;

  return (
    <SlidePanel
      ref={slidePanelRef}
      onClose={onClose}
      className="notif-slide-panel nt-panel"
      width={440}
    >
      {/* Bailey's card: what needs you, and setup while it's unfinished. */}
      <div className="nt-hero">
        <span className="nt-hero-glow" aria-hidden="true" />
        <div className="nt-hero-top">
          {!baileyOff && <span className="nt-hero-face" aria-hidden="true"><BaileyStill appearance={look || undefined} face /></span>}
          <div className="nt-hero-text">
            <span className="nt-hero-kicker">{baileyOff ? 'Notifications' : 'From Bailey'}</span>
            <h2 className="nt-hero-title">{heroTitle}</h2>
          </div>
          <button type="button" className="nt-hero-close" onClick={closeWithAnim} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </button>
        </div>
        {todos.setup && (
          <div className="nt-setup">
            <SetupRing done={todos.setup.done} total={todos.setup.total} />
            <div className="nt-setup-text">
              <b>{todos.setup.title || 'Finish setting up'}</b>
              <small>{todos.setup.detail}</small>
            </div>
            <button type="button" className="nt-setup-go" onClick={() => go({ run: todos.setup.open })}>
              {todos.setup.go || 'Continue'}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </button>
          </div>
        )}
      </div>

      <div className="nt-seg" role="tablist" aria-label="Notifications">
        <button type="button" role="tab" aria-selected={tab === 'todo'} className={tab === 'todo' ? 'is-on' : ''} onClick={() => { hapticTick(); setTab('todo'); }}>
          To do{todos.rows.length + (stripe.active ? 1 : 0) > 0 && <span className="nt-count">{todos.rows.length + (stripe.active ? 1 : 0)}</span>}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'updates'} className={tab === 'updates' ? 'is-on' : ''} onClick={() => { hapticTick(); setTab('updates'); }}>
          Updates{unread > 0 && <span className="nt-count is-soft">{unread > 9 ? '9+' : unread}</span>}
        </button>
      </div>

      <div
        ref={scrollRef}
        className="notif-scroll nt-scroll"
        style={{
          transform: pull.distance > 0 ? `translate3d(0, ${pull.distance}px, 0)` : undefined,
          transition: pull.distance === 0
            ? 'transform 280ms cubic-bezier(0.2, 0.9, 0.3, 1)'
            : 'none',
        }}
      >
        <div
          className={`notif-pull ${pull.refreshing ? 'notif-pull--spinning' : ''}`}
          style={{
            opacity: Math.min(pull.progress, 1),
            transform: `translate3d(-50%, ${pull.distance - 36}px, 0) rotate(${pull.progress * 270}deg)`,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
            <path d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14 2v3.5H10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {tab === 'todo' && (
          <div className="nt-todos">
            {stripe.active && (
              <button type="button" className={`ss-notif is-${stripe.status.tone}`} onClick={fixStripe}>
                <span className="ss-notif-icon" aria-hidden="true"><WarnMark /></span>
                <span className="ss-notif-body">
                  <span className="ss-notif-title">{stripe.status.key === 'restricted' ? 'Your Stripe account is paused' : 'Your Stripe account needs attention'}</span>
                  <span className="ss-notif-sub">{stripe.status.key === 'restricted' ? 'You cannot take payments or withdraw until this is sorted.' : `You may be unable to take payments or withdraw until this is fixed. ${needsSentence(stripe.status)}`}</span>
                </span>
                <span className="ss-notif-cta">{stripe.status.key === 'restricted' ? 'See details' : 'Fix now'}</span>
              </button>
            )}
            {todos.rows.map((row) => (
              <div className="nt-todo" key={row.id}>
                <span className="nt-tile"><TodoIcon kind={row.kind} /></span>
                <div className="nt-todo-text">
                  <span className="nt-todo-kind">{row.label}</span>
                  <b>{row.title}</b>
                  {row.sub && <small>{row.sub}</small>}
                </div>
                <button type="button" className="nt-act" onClick={() => go(row)}>{row.action}</button>
              </div>
            ))}
            {!todos.rows.length && !stripe.active && (
              <div className="nt-clear">
                <span className="nt-clear-tick" aria-hidden="true">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                </span>
                <b>You're all caught up</b>
                <small>{baileyOff ? 'Anything that needs you will show here.' : 'Bailey will put anything that needs you here.'}</small>
              </div>
            )}
          </div>
        )}

        {tab === 'updates' && (
          <>
            <div className="nt-upd-actions">
              <button type="button" className="notif-header-btn notif-header-btn--refresh" onClick={onRefresh} aria-label="Refresh notifications">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14 2v3.5H10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {unread > 0 && <button type="button" className="notif-header-btn notif-header-btn--mark" onClick={handleMarkAllRead}>Mark all read</button>}
            </div>
            {loading && !hasItems && <SkeletonRows />}
            {!loading && !hasItems && <EmptyState />}
            {hasItems && (
              <div className="notif-list">
                {renderGroup('Today', grouped.today, 0)}
                {renderGroup('Yesterday', grouped.yesterday, todayCount)}
                {renderGroup('Earlier', grouped.earlier, todayCount + yesterdayCount)}
              </div>
            )}
          </>
        )}
      </div>
    </SlidePanel>
  );
};

export default NotificationPanel;
